import { type ReactElement, type ReactNode, useCallback, useEffect } from 'react'
import { useSnapshot } from 'valtio'
import Router from 'next/router'
import { globalLayoutProxy } from '../../../proxies/globalLayoutProxy'
import { GlobalLoader } from '../../ui/global/GlobalLoader'
import { GlobalAuthError } from '../../ui/global/GlobalAuthError'
import { Navbar } from '../../navigation/Navbar'
import { Sidebar } from '../../navigation/Sidebar'
import { globalUserProxy } from '../../../proxies/globalUserProxy'
import { APP_SETTINGS_ACCOUNT_PATH } from '../../../../pages/app/settings/account'
import { AutoLogoutModal } from '../../navigation/AutoLogoutModal'
import { handleCriticalSignOut, handleSignOut } from '../../navigation/Navbar/Account/components/utils'
import { handleStorageChange } from '../../../utils/authUtils'
import { gtmTrackUserLogin } from '../../../utils/gtmUtils'
import { executeRetrieveMerchants } from './executeRetrieveMerchants'
import { executeRetrieveUser } from './executeRetrieveUser'
import { ChangePasswordBanner } from './ChangePasswordBanner'
import { UpdateVersionBanner } from './UpdateVersionBanner/UpdateVersionBanner'

interface LayoutProps {
  children: ReactNode
  shouldDisplay: boolean
}

export function ContainerWrapper ({ children, shouldDisplay }: LayoutProps): ReactElement | null {
  const { apiCall: { loading, error } } = useSnapshot(globalUserProxy)
  const { sidebarExpanded } = useSnapshot(globalLayoutProxy)
  const toggleExpanded = useCallback(
    () => { globalLayoutProxy.sidebarExpanded = !globalLayoutProxy.sidebarExpanded },
    []
  )
  useEffect(() => {
    async function internalFunc (): Promise<void> {
      const user = await executeRetrieveUser()

      gtmTrackUserLogin({ userId: String(user?.id) })

      if (!user) {
        void handleSignOut()
        return
      }

      const selectedMerchant = await executeRetrieveMerchants(user)
      if (!selectedMerchant) {
        void handleCriticalSignOut({ merchantId: user.ppMerchantId, userId: user.id })
        return
      }

      if (globalUserProxy.user?.passwordNeedsReset) {
        void Router.push(APP_SETTINGS_ACCOUNT_PATH)
      }
      window.addEventListener('storage', handleStorageChange)
    }
    void internalFunc()
  }, [])

  if (!shouldDisplay) { return null }
  if (loading) { return <GlobalLoader /> }
  if (error) { return <GlobalAuthError error={error} /> }

  return (
    <>
      <UpdateVersionBanner />
      <ChangePasswordBanner shouldDisplay={globalUserProxy.user?.passwordNeedsReset} />
      <AutoLogoutModal />
      <Navbar toggleExpanded={toggleExpanded} />
      <div className="flex min-h-full">
        <Sidebar expanded={sidebarExpanded} />
        <main className="w-full px-12 py-8">{children}</main>
      </div>
    </>
  )
}
